/* global $ */
// jQuery, loaded by the page layout (used by the autofill auto-select below).
import React, { useState } from "react";

import Autocomplete from "@mui/material/Autocomplete";
import MuiTextField from "@mui/material/TextField";
import { isEqual } from "lodash";

import usStates from "@us-states";
import {
  filterStateOptions,
  findExactStateMatch,
} from "~/shared/utils/usStateOptions.js";

// Territories a patient can sign up from (they're in us_states.json) but that
// Kick does not ship to. Kept out of every shipping-address picker. Neither is in
// prescribable_states.json either, so mail-order checkout and the EMR address
// edit (IsStatePrescribable) would refuse them anyway.
export const NON_SHIPPABLE_STATE_CODES = ["MH", "MP"];

// Shipping-address states, built from app/config/us_states.json (@us-states),
// the list signup and the server share. The "Name  (XX)" label, double space
// included, is the format this picker has always shown; saved addresses keep
// only the abbreviation and are looked up by it, so labels are display-only.
export const usStatesList = usStates
  .filter(({ code }) => !NON_SHIPPABLE_STATE_CODES.includes(code))
  .map(({ code, name }) => ({
    name: `${name}  (${code})`,
    abbreviation: code,
  }));

const shippingOptionAccessors = {
  getCode: (option) => option.abbreviation,
  getLabel: (option) => option.name,
};

function getOptionData(text) {
  if (!text) return null;
  if (text.length === 2) {
    const state = usStatesList.find(
      (state) => state.abbreviation.toLowerCase() === text.toLowerCase(),
    );
    return state;
  }
  if (text.length > 2) {
    return usStatesList.find((state) =>
      state.name.toLowerCase().includes(text.toLowerCase()),
    );
  }
}

// `error` and `helperText` go to the input, not to Autocomplete (which would
// put them on its root div): the field shows a validation error the way every
// other MUI field does (baseStyles.js fieldError*, applied by patientTheme).
export const AutocompleteUSStates = ({
  error = false,
  helperText,
  ...props
}) => {
  const [prevValue, setPrevValue] = useState("");
  // What's typed, so an exact code or name ("IN", "south carolina") is highlighted and Enter /
  // blur (autoSelect) picks it; otherwise nothing is pre-highlighted.
  const [typed, setTyped] = useState("");
  return (
    <Autocomplete
      {...props}
      autoComplete={true}
      autoSelect={true}
      options={usStatesList}
      onInputChange={(event, value, reason) => {
        setTyped(value);
        props.onInputChange?.(event, value, reason);
      }}
      autoHighlight={
        findExactStateMatch(usStatesList, typed, shippingOptionAccessors) !==
        undefined
      }
      filterOptions={(options, state) =>
        filterStateOptions(options, state.inputValue, shippingOptionAccessors)
      }
      getOptionLabel={(option) => option.name || ""}
      isOptionEqualToValue={(option, value) => {
        return isEqual(option, value);
      }}
      renderInput={(params) => {
        function tryAutoSelect(e) {
          const validatedEvtValue = e.target.value.replace(/[^a-zA-Z]/g, ""); // remove non-alphabetic characters
          const optionData = getOptionData(validatedEvtValue);
          if (optionData) {
            // autoselect (for browser autofill and paste)
            params.inputProps.onChange(e);
            props.onChange(optionData);
            setTimeout(() => {
              $(
                `.MuiAutocomplete-option:contains("${optionData.name}")`,
              ).trigger("click");
              setPrevValue(validatedEvtValue); // set after trigger "click"
            }, 0);
          } else {
            setPrevValue(validatedEvtValue);
            params.inputProps.onChange(e);
          }
        }
        return (
          <MuiTextField
            label={params.inputProps.value ? "" : "Select state"} // hide label if value is selected
            variant={props.variant}
            {...params}
            error={error}
            helperText={helperText}
            inputProps={{
              ...params.inputProps,
              name: "state",
              id: "state",
              onChange: (e) => {
                const validatedEvent = { ...e };
                if (validatedEvent.target) {
                  validatedEvent.target.value =
                    validatedEvent?.target?.value?.replace(/[^a-zA-Z]/g, "");
                }
                // if diff === 1, then it's typed input and dont check for autoselect
                if (
                  Math.abs(
                    validatedEvent?.target?.value?.length - prevValue.length,
                  ) > 1
                ) {
                  tryAutoSelect(e);
                } else {
                  setPrevValue(validatedEvent?.target?.value);
                  params.inputProps.onChange(validatedEvent);
                }
              },
            }}
            InputLabelProps={{ shrink: false }}
          />
        );
      }}
      onChange={(_, data) => {
        setPrevValue(data?.name || "");
        props.onChange(data);
      }}
    />
  );
};

// export default AutocompleteUSStates;
